import React, { useState, Fragment, useCallback, useEffect } from 'react';
import { compose } from 'redux';
import { connect, batch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import uuid from 'uuid/v1';
import {
  List,
  ListBody,
  ListHeader,
  ListHeaderItem,
  ListItemColumn,
  Spinner,
  SVGIcon,
} from '@pearsontechnology/aero-components';

import meShape from 'Workspace/types/me';
import enhanceWithNavigation from 'hocs/enhanceWithNavigation';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import publicationService from 'services/PublicationService';
import exportService from 'services/ExportService';
import activityService from 'services/ActivityService';
import { selectCurrentMe } from 'data/CurrentUser/selectors';

import ExportETextAndChannelPartner from 'Workspace/Publications/components/PublicationModal/ExportETextAndChannelPartner';
import DeleteConfirmModal from 'Workspace/Publications/components/PublicationModal/DeleteConfirm';
import PublicationAlertModal from 'Workspace/Publications/components/PublicationModal/PublicationAlertModal';
import ExportClassOnDemand from 'Workspace/Publications/components/PublicationModal/ExportClassOnDemand';
import ExportPearsonPlus from 'Workspace/Publications/components/PublicationModal/ExportPearsonPlus';
import OutputInfoClassOnDemandModal from 'Workspace/Publications/components/PublicationModal/OutputInfoClassOnDemandModal';
import UnauthorizedModal from 'Workspace/Publications/components/PublicationModal/UnauthorizedModal';
import PublicationListItemActions from 'Workspace/Publications/components/PublicationListItemActions';
import PublicationHistory from 'Workspace/Publications/components/PublicationHistory';
import { getFilteredPublicationList, selectPublicationsSort } from 'Workspace/Publications/store/selectors';
import { updatePublicationsList, updatePublicationsStatus, setPublicationsSort, updatePublicationsDetail, setPublicationsHistoryForActivity } from 'Workspace/Publications/store/actions';
import { getTime, stripTags, sortByDate } from 'Workspace/Publications/utils';
import { createRandomString } from 'utils/createRandomString';
import { status, sortType, headerKey, timeFormats, publishFormat, actionType } from 'Workspace/Publications/utils/constants';
import { ActivityName, ColoredText, PublishColumnText, PublishColumnContainer, PublishColumnButton, ExpandButton, SortButton, ButtonListItemColumn, ActivityNameContainer, OutputTypeContainer, OutputTypeItem, StyledListItem } from './styles';
import messages from './messages';
import { dispatch } from "pluginServices/store";
import { pushToast } from "containers/ToastManager/actions";
import { featureKeys } from 'services/FeatureFlagService';
import enhanceWithFeatureFlagSubscription from "hocs/enhanceWithFeatureFlagSubscription";

const LinkListItemColumn = enhanceWithNavigation(ListItemColumn);

/**
 * @desc Renders Publications list
 */
const PublicationList = ({
  publicationsList,
  theme,
  me,
  setSort,
  sortState,
  setHistory,
  updatePublicationHistory,
  deletePublicationHistory,
  updatePublicationStatus,
  getFeatureFlagValue,
}) => {
  const [expandedList, setExpandedList] = useState({});
  const [configSettings, setConfigSettings] = useState({});
  const [publicationToPublish, setPublicationToPublish] = useState({});
  const [publicationToDelete, setPublicationToDelete] = useState({});
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [unauthorizedVisible, setUnauthorizedVisible] = useState(false);
  const [outputInfoModalVisible, setOutputInfoModalVisible] = useState(false);
  const [publicationAlertVisible, setPublicationAlertVisible] = useState(false);
  const [outputInfo, setOutputInfo] = useState({});
  const [exportStatus, setExportStatus] = useState(null);
  const [oculusStatus, setOculusStatus] = useState(null);
  const [exportSnippetsCompleted, setExportSnippetsCompleted] = useState(0);
  const [totalExportSnippet, setTotalExportSnippet] = useState(0);
  const [inProgressList, setInProgressList] = useState([]);
  const [subscribedList, setSubscribedList] = useState([]);
  const [disabledTocLevel, setDisabledTocLevel] = useState('');
  const [showVersionCompareUI, setShowVersionCompareUI] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState({});
  const [startingPublish, setStartingPublish] = useState(null);
  const { NAME, AUTHOR, LAST_MODIFIED, PUBLISHED_BY } = headerKey;
  const isClassOnDemand = publicationToPublish.outputType === publishFormat.CLASS_ON_DEMAND;
  const isPearsonPlus = publicationToPublish.outputType === publishFormat.PEARSON_PLUS;
  const isPearsonPlusTest = publicationToPublish.outputType === publishFormat.PEARSON_PLUS_TEST;
  const isEpubOrChannelPartner = (publicationToPublish.outputType !== publishFormat.CLASS_ON_DEMAND) && (publicationToPublish.outputType !== publishFormat.PEARSON_PLUS) && (publicationToPublish.outputType !== publishFormat.PEARSON_PLUS_TEST);

  /* If you  refresh the page/ reopen the URL through new tab right after publication and its still "publishing",
when you come back, the useeffect is to update the status once the publication is done */
  useEffect(() => {
    if (inProgressList && inProgressList.length > 0) {
      inProgressList.forEach((publication) => {
        const payload = {
          publication,
          format: publication.version,
        };
        if (!subscribedList.includes(publication.publicationId)) {
          publicationService.subscribeToPublicationJob(publication.publicationId, consumePublishBroadcast(payload));
          setSubscribedList((prevItems) => [...prevItems, publication.publicationId]);
        }
      });
    }
  }, [consumePublishBroadcast, inProgressList, subscribedList]);

  const publishAlertDismissHandler = useCallback(() => {
    setPublicationAlertVisible(false);
  });

  /**
   * @desc Dismiss publish modal call back
   */
  const dismiss = () => {
    setExportModalVisible(false);
    setDeleteModalVisible(false);
    setOutputInfoModalVisible(false);
    setExportStatus(null);
    setExportSnippetsCompleted(0);
    setTotalExportSnippet(0);
  };

  /**
   * @desc Sets the sort state for publicationsList
   * @param {string} key publicationsList property that needs to be sorted
   * @param {string} type Determines what type sorting is needed - date/string/number
   */
  const sortBy = (key, type = 'string') => () => {
    let order = sortType.ASC;
    if (sortState?.order) {
      order = sortState.order === order ? sortType.DESC : order;
    }
    setSort({ key, type, order });
  };

  /**
   * @desc Opens output info modal
   * @param {string} activityId
   * @param {string} outputType
   */
  const outputInfoActionCallback = useCallback(async (activityId, outputType, market) => {
    //TO DO ? only CLASS ON DEMAND and PUBLISHED - market may not be needed here.
    const { publications: publicationHistory } = await publicationService.getPublicationHistory(activityId, outputType, market);
    const sortedpublicationHistories = sortByDate(publicationHistory);
    const parsedConfig = JSON.parse(sortedpublicationHistories[0].config);
    const { settings: recentPublicationHistory } = parsedConfig;
    setOutputInfo({ ...recentPublicationHistory, outputType });
    setOutputInfoModalVisible(true);
  }, []);

  /**
   * @desc get publication history and displayExport modal popup
   * @param {object} publication //currently selected course details
   */
  const displayExportModal = async (publication) => {
    await getPublicationHistory(publication.activityId, publication.outputType, publication.market);
    setExportModalVisible(true);
  };

  const isOKToPublish = async (publication) => {
    let okToPublish = true;
    const forceAllowPublish = getFeatureFlagValue(featureKeys.WORKSPACE_FORCE_ALLOW_PUBLISH);
    if (forceAllowPublish) {
      return okToPublish;
    }
    const statusesToLock = [status.PUBLISH_STARTED, status.INPROGRESS];
    if (!publication.history) {
      const { publications } = await publicationService.getPublicationHistory(publication.activityId, publication.outputType, publication.market);
      if (publications) {
        let sortedPublications = sortByDate(publications);
        setHistory(sortedPublications, publication.activityId, publication.outputType, publication.market);
        if (sortedPublications.length && sortedPublications[0]?.publicationJobStatus === undefined) {  // until BACW-235 is fixed from BE
          okToPublish = false;
        }
        if (statusesToLock.includes(sortedPublications[0]?.publicationJobStatus)) {
          okToPublish = false;
        }
      }
    } else {
      if (publication.history.length && publication.history[0]?.publicationJobStatus === undefined) {  // until BACW-235 is fixed from BE
        okToPublish = false;
      }
      if (statusesToLock.includes(publication.history[0]?.publicationJobStatus)) {
        okToPublish = false;
      }
    }
    return okToPublish;
  };

  /**
   * @desc Opens course publish confirmation modal
   * @param {object} publication //currently selected course details
   */
  const openModal = (publication, action) => async () => {
    setStartingPublish({ activityId: publication.activityId, outputType: publication.outputType, market: publication?.market });
    const permission = await publicationService.isPublicationOwner(publication.activityId);
    if (!permission) {
      setUnauthorizedVisible(true);
      setStartingPublish(null);
    } else {
      if (action === actionType.PUBLISH) {
        const isOKToStartPublish = await isOKToPublish(publication);
        if (!isOKToStartPublish) {
          dispatch(pushToast({ message: intl.formatMessage(messages.ALREADY_IN_PROGRESS) }));
          setStartingPublish(null);
          return;
        }
        setPublicationToPublish({ ...publication });
        if (publication.outputType === publishFormat.CHANNEL_PARTNER) {
          setPublicationAlertVisible(true);
        } else {
          await displayExportModal(publication);
        }
      } else if (action === actionType.DELETE) {
        setPublicationToDelete({ ...publication });
        setDeleteModalVisible(true);
      }
      setStartingPublish(null);
    }
  };

  /**
   * @desc publication alert confirmation modal
   * @param {object} publication //currently selected publication details
   */
  const publishAlertConfirm = (publication) => () => {
    setPublicationAlertVisible(false);
    displayExportModal(publication);
  };

  /**
   * Fetch publication history version's for an activity
   * @param {string} activityId
   * @param {string} outputType
   */
  const getPublicationHistory = async (activityId, outputType, market) => {
    const startedPublications = [];
    const { publications } = await publicationService.getPublicationHistory(activityId, outputType, market);
    if (publications) {
      let sortedPublications = sortByDate(publications);
      checkForLockingTOC(sortedPublications);
      setHistory(sortedPublications, activityId, outputType, market);
      try {
        let recentPublication = sortedPublications[0];
        const bookId = recentPublication?.bookId;
        if (outputType === publishFormat.PEARSON_PLUS && bookId) {
          const { oculusStatus } = await publicationService.getOculusStatus(bookId);
          setOculusStatus(oculusStatus);
        }
        let configStr = recentPublication?.config;
        let configJSON = configStr && configStr.length > 0 ? JSON.parse(configStr) : {};
        setConfigSettings(configJSON.settings);
      } catch (err) {
        //Ignore this error
      }
      //keep a list of inProgress
      sortedPublications.forEach((publication) => {
        if (!publication?.publicationJobStatus || publication?.publicationJobStatus === 'STARTED') {
          startedPublications.push(publication);
        }
      });
      if (startedPublications.length > 0) {
        setInProgressList(startedPublications);
      }
    }
  };

  const checkForLockingTOC = (sortedPublications) => {
    try {
      setDisabledTocLevel("");
      const publicationLockToc = getFeatureFlagValue(
        featureKeys.WORKSPACE_PUBLICATION_LOCKTOC
      );
      if (publicationLockToc) {
        const statusesWhichLockTOC = [status.COMPLETED, status.PEARSONPLUS_TITLE_INGEST_COMPLETE, status.INDEX_IN_PROGRESS];
        for (let i = 0; i < sortedPublications.length; i++) {
          const { publicationJobStatus, description } = sortedPublications[i];
          const isLockableStatus = statusesWhichLockTOC.some((status) => publicationJobStatus?.includes(status));
          if (description && isLockableStatus) {
            const { tocLevel } = JSON.parse(description);
            if (tocLevel) {
              setDisabledTocLevel(tocLevel);
              break;
            }
          }
        }
      }
    } catch (error) {
      dispatch(pushToast({ message: error?.message }));
    }
  };

  /**
   * @desc Handle publish and status update
   * @param {Object} payload
   */
  const publish = async (payload) => {
    // To do in future ticket - Genericize this publish code for handling any publishFormat
    if (payload.format === publishFormat.CLASS_ON_DEMAND) {
      //classOnDemand Publish does not consume ExportBroadcast but consume PublishBroadcast.
      const response = await publicationService.publish(payload, null, consumePublishBroadcast(payload));
      if (response?.error) {
        return response;
      }
      setExportModalVisible(false);
    } else {
      setExportStatus(status.INPROGRESS);
      const totalExportSnippets = await publicationService.publish(payload, consumeExportBroadcast(payload), consumePublishBroadcast(payload));
      if (totalExportSnippets) {
        setTotalExportSnippet(totalExportSnippets);
      } else {
        dismiss();
      }
    }
  };

  const _getPublishStatus = (exportStatus) => (exportStatus === status.COMPLETE ?
    {
      publicationJobStatus: status.PUBLISH_STARTED,
      statusMessage: '10002',
    } : {
      publicationJobStatus: exportStatus,
      statusMessage: '10005',
    }
  );

  const getActivityAuthor = async (activityId) => {
    const { config } = await activityService.getActivity(activityId);
    return stripTags(config.author);
  };

  /**
   * @desc export broadcast callback
   * @param {object} payload :  publication payload
   * @param {object} response : export broadcast callback payload
   */
  const consumeExportBroadcast = (payload) => ({ response }) => {
    const { exportId, progress } = response;
    const exportStatus = status[progress];
    if ([status.COMPLETE, status.ERROR].includes(exportStatus)) {
      exportService.unSubscribeToExport(exportId);
      setExportStatus(exportStatus);
      const { publicationJobStatus, statusMessage } = _getPublishStatus(exportStatus);
      let market = '';
      if (payload.market) {
        market = payload.market;
      } else {
        //payload.publication.format = "EPUB_ETEXT:College"
        market = payload.publication.format.split(':')[1];
      }
      updatePublicationStatus({
        ...payload.publication,
        publicationJobStatus,
        statusMessage,
        market,
      });
    } else {
      setExportSnippetsCompleted((prevState) => prevState + 1);
    }
  };

  /**
   * @desc publish broadcast callback
   * @param {object} payload :  publication payload
   * @param {object} response : publication broadcast callback payload
   */
  const consumePublishBroadcast = useCallback((payload) => async ({ response }) => {
    const { publicationId, publicationJobStatus, statusMessage } = response;
    const jobStatus = status[publicationJobStatus];
    const passingStatus = [status.COMPLETED, status.FAILED, status.PEARSONPLUS_TITLE_INGEST_COMPLETE, status.INDEX_IN_PROGRESS];
    if (passingStatus.includes(jobStatus)) {
      publicationService.unSubscribeToPublicationJob(publicationId);

      // The inProgressList(subscribeToPublicationJob) publications ( on page refresh ) have different .version and .market than when starting from onPublish().subscribeToPublicaitonJob
      // This code ensures both are same for history
      let market = '';
      if (payload.market) {
        market = payload.market;  //"College"
      } else {
        // payload.publication.version = "EPUB_ETEXT:College"
        market = payload.publication.version.split(':')[1];
      }
      payload.publication.version = payload.publication.version.split(':')[0]; // "EPUB_ETEXT" - we want *just* version, not the market

      const { bookId, etextVersion } = response;
      updatePublicationStatus({
        ...payload.publication,
        publicationJobStatus: jobStatus,
        statusMessage,
        bookId,
        etextVersion,
        publicationId,
        market,
      });

      // Get the updated status of the publications
      const { publications } = await publicationService.getPublicationHistory(payload.publication.activityId, payload.publication.version, payload.publication.market);
      const sortedPublications = sortByDate(publications);
      setHistory(sortedPublications, payload.publication.activityId, payload.publication.version, payload.publication.market);
    }
  });

  /**
   * @desc Defensive coding for publication regarding metadata property
   * @param {object} metadata :  Object containing the user selections for landmarks (only relevant for Channel Partner Epub)
   * @param {string} format :  Format of publication
   */

  const handleMetadata = (metadata, outputType, market) => {
    if ((outputType === publishFormat.CHANNEL_PARTNER || outputType === publishFormat.IN_DESIGN) && metadata?.landmarks) {
      const { brief, start, backmatter } = metadata.landmarks;
      const landmarks = {
        isbn: metadata.isbn,
        childISBN: metadata.childISBN,
        isIsbnSkipped: metadata.isIsbnSkipped,
        isValidationSkipped: metadata.isValidationSkipped,
        landmarks: {
          brief,
          start,
          backmatter,
        },
        ...(market && { market }),
      };
      return JSON.stringify(landmarks);
    } else if (outputType === publishFormat.EPUB) {
      const etextMetadata = {
        tocLevel: metadata.tocLevel,
        ...(market && { market }),
      };
      return JSON.stringify(etextMetadata);
    } else {
      return metadata;
    }
  };

  /**
   * @desc Modal on publish handler
   * @param {string} format :  format of publication
   * @param {string} type :  type of publication
   * @param {string} note :  note in publication
   */
  // NOTE: Eventually the format item should be removed as it should now come from publicationToPublish.outputType
  const onPublish = async ({ format, type, tocLevel, note, metadata, market, productId, settings = {} }) => {
    const { activityId, title, outputType } = publicationToPublish;
    const author = await getActivityAuthor(activityId);
    const description = {
      note,
      type,
      tocLevel,
    };

    const publication = {
      activityId,
      accountId: me.accountId,
      author,
      description: JSON.stringify(description),
      publicationId: uuid(), // Adding temp publicationId
      isDeleteDisabled: true, // Added to restrict delete functionality until we get correct publicationId from BE
      publisherGivenName: me.givenName,
      publisherFamilyName: me.familyName,
      publicationJobStatus: status.INPROGRESS,
      statusMessage: '10006',
      title,
      metadata: handleMetadata(metadata, outputType, market),
      updatedAt: new Date(),
      ...((Object.keys(settings).length && { settings })),
      productId,
      version: format,
      ...(market && { market }),
      ltiConsumerCredential: {
        key: createRandomString(24),
        secret: createRandomString(36),
      },
    };

    await onToggle(publicationToPublish, true)();
    // publication added as a version
    updatePublicationHistory(publication);
    const payload = {
      publication,
      format: publicationToPublish.outputType,
      market: market,
    };
    //no export ID till here.
    publish(payload);
  };

  /**
   * @desc Delete publication history version.
   */
  const onDelete = async () => {
    const response = await publicationService.deletePublication(publicationToDelete);
    setDeleteModalVisible(false);
    if (!response.error) {
      deletePublicationHistory(publicationToDelete);
    }
  };

  /**
   * @desc Publications history dropdown toggle handler
   * @param {string} activityId
   * @param {boolean} expand
   */
  const onToggle = (publicationItem, expand = false) => async () => {
    const { activityId, outputType, market } = publicationItem;
    const uniqueKey = `${publicationItem.activityId}${publicationItem.outputType}${publicationItem?.market || ''}`;
    const newExpandedData = { ...expandedList };
    const isExpanded = newExpandedData[uniqueKey];

    if (isExpanded) {
      // if drawer is expanded do nothing
      if (expand) return;

      delete newExpandedData[uniqueKey];
      setExpandedList(newExpandedData);
    } else {
      newExpandedData[uniqueKey] = publicationItem;
      setExpandedList(newExpandedData);
      await getPublicationHistory(activityId, outputType, market);
    }
  };

  const isMasteringProductIdUnique = async (activityId) => {
    const response = await publicationService.isMasteringProductIdUniqueService(activityId);
    if (response?.error?.code === 422) {
      return false;
    }
    return true;
  };

  const productIdPDZ = async (payload) => await publicationService.productIdPDZ(payload);

  const handleVersionChangesClick = useCallback((publicationItem) => {
    setShowVersionCompareUI(true);
    setSelectedPublication(publicationItem);
    setTimeout(onToggle(publicationItem, true), 0);
  });

  const hanldeVersionUiActions = (showDiffUI) => {
    setShowVersionCompareUI(showDiffUI);
  };

  return (<>
    <List>
      <ListHeader>
        <ListItemColumn colWidth="30%" colWidthMobile="30%">
          <ListHeaderItem>{intl.formatMessage(messages.name)}</ListHeaderItem>
          <SortButton id={NAME} sort={sortState} onClick={sortBy(NAME)} />
        </ListItemColumn>
        <ListItemColumn colWidth="65px" colWidthMobile="10%">
        </ListItemColumn>
        <ListItemColumn colWidth="20%" colWidthMobile="20%">
          <ListHeaderItem>{intl.formatMessage(messages.author)}</ListHeaderItem>
          <SortButton id={AUTHOR} sort={sortState} onClick={sortBy(AUTHOR)} />
        </ListItemColumn>
        <ListItemColumn colWidth="20%" colWidthMobile="20%">
          <ListHeaderItem>{intl.formatMessage(messages.owner)}</ListHeaderItem>
          <SortButton id={PUBLISHED_BY} sort={sortState} onClick={sortBy(PUBLISHED_BY)} />
        </ListItemColumn>
        <ListItemColumn colWidth="20%" colWidthMobile="20%">
          <ListHeaderItem>{intl.formatMessage(messages.updatedAt)}</ListHeaderItem>
          <SortButton id={LAST_MODIFIED} sort={sortState} onClick={sortBy(LAST_MODIFIED, 'date')} />
        </ListItemColumn>
        <ListItemColumn colWidth="10%" colWidthMobile="10%">
        </ListItemColumn>
        <ListItemColumn colWidth="30px" colWidthMobile="30px" grow="0">
        </ListItemColumn>
      </ListHeader>
      <ListBody>
        {publicationsList.map((publicationItem, i) => {

          const disablePublicationItem = showVersionCompareUI && (publicationItem.activityId !== selectedPublication.activityId || selectedPublication.outputType !== publicationItem.outputType);
          const uniqueKey = `${publicationItem.activityId}${publicationItem.outputType}${publicationItem?.market || ''}`;

          return <Fragment key={`courseware-item-${uniqueKey}${i}`}>
            <StyledListItem
              disabled={disablePublicationItem}
              isSelected={showVersionCompareUI}
            >
              <ListItemColumn colWidth="65px" colWidthMobile="10%">
                {/*
                - Newly 'Add' ed publication instance with no history shouldn't show ExpandButton.
                - For published activities it will have a history for sure, so show ExpandButton.
               */}
                {(!publicationItem.unPublishedInstance || publicationItem.history.length > 0) && <ExpandButton
                  expanded={expandedList[uniqueKey]}
                  onClick={onToggle(publicationItem)}
                />}
              </ListItemColumn>
              <LinkListItemColumn colWidth="30%" colWidthMobile="30%" path={`course/${publicationItem.activityId}/content`}>
                <ColoredText color={theme['color.purpleLight']}>
                  <SVGIcon
                    icon="duotoneCourse"
                    viewBox="0 0 24 24"
                  />
                </ColoredText>
                <ActivityNameContainer>
                  <ActivityName>
                    {publicationItem.title}
                  </ActivityName>
                  {publicationItem.outputType &&
                    <OutputTypeContainer>
                      <OutputTypeItem>
                        {messages[publicationItem.outputType] && intl.formatMessage(messages[publicationItem.outputType])}
                      </OutputTypeItem>
                      {publicationItem?.market &&
                        <OutputTypeItem>
                          {`${publicationItem.market}`}
                        </OutputTypeItem>
                      }
                    </OutputTypeContainer>
                  }
                </ActivityNameContainer>
              </LinkListItemColumn>
              <ListItemColumn colWidth="20%" colWidthMobile="20%">
                <PublishColumnText>{publicationItem.author}</PublishColumnText>
              </ListItemColumn>
              <ListItemColumn colWidth="20%" colWidthMobile="20%">
                <PublishColumnText>
                  {`${publicationItem.publisherGivenName} ${publicationItem.publisherFamilyName}`}
                </PublishColumnText>
              </ListItemColumn>
              <ListItemColumn colWidth="20%" colWidthMobile="20%">
                <PublishColumnText>
                  {publicationItem.updatedAt ? getTime(publicationItem.updatedAt, timeFormats.DATE_MONTH) : ''}
                </PublishColumnText>
              </ListItemColumn>
              <ButtonListItemColumn colWidth="10%" colWidthMobile="10%">
                <PublishColumnButton disabled={showVersionCompareUI ||
                  (startingPublish && publicationItem.activityId === startingPublish.activityId && publicationItem.outputType === startingPublish.outputType && publicationItem?.market === startingPublish?.market)} onClick={openModal(publicationItem, 'PUBLISH')} role="button">
                  <PublishColumnContainer>
                    {startingPublish && publicationItem.activityId === startingPublish.activityId && publicationItem.outputType === startingPublish.outputType && (publicationItem?.market === startingPublish?.market) && <Spinner xsmall />}
                    <ColoredText color={theme['color.primary']}>
                      {intl.formatMessage(messages.publish)}
                    </ColoredText>
                  </PublishColumnContainer>
                </PublishColumnButton>
              </ButtonListItemColumn>
              <ListItemColumn colWidth="30px" colWidthMobile="30px" grow="0">
                <PublicationListItemActions
                  outputInfoActionCallback={outputInfoActionCallback}
                  publicationItem={publicationItem}
                  vesionChangesCallback={handleVersionChangesClick}
                  disabled={showVersionCompareUI}
                  getFeatureFlagValue={() => { }}
                />
              </ListItemColumn>
            </StyledListItem>
            {expandedList[uniqueKey] && <PublicationHistory
              showCompareVersionUI={showVersionCompareUI && !disablePublicationItem}
              historyList={publicationItem.history}
              actionHandler={openModal}
              show={true}
              hanldeVersionUiActions={hanldeVersionUiActions}
              disabled={disablePublicationItem}
            />}
          </Fragment>;
        })
        }
      </ListBody >
    </List >

    {outputInfoModalVisible && outputInfo.outputType === publishFormat.CLASS_ON_DEMAND &&
      <OutputInfoClassOnDemandModal
        visible={outputInfoModalVisible}
        outputInfo={outputInfo}
        onDismiss={dismiss}
      />
    }

    {isClassOnDemand && <ExportClassOnDemand
      visible={exportModalVisible}
      configSettings={configSettings}
      onDismiss={dismiss}
      onPublish={onPublish}
      isMasteringProductIdUnique={isMasteringProductIdUnique}
    />}

    {isPearsonPlus && <ExportPearsonPlus
      visible={exportModalVisible}
      configSettings={configSettings}
      onDismiss={dismiss}
      onPublish={onPublish}
      exportStatus={exportStatus}
      oculusStatus={oculusStatus}
      exportSnippetsCompleted={exportSnippetsCompleted}
      publicationToPublish={publicationToPublish}
      totalExportSnippet={totalExportSnippet}
      productIdPDZ={productIdPDZ}
      market={publicationToPublish?.market}
      disabledTocLevel={disabledTocLevel}
    />}

    {isPearsonPlusTest && <ExportPearsonPlus
      visible={exportModalVisible}
      configSettings={configSettings}
      onDismiss={dismiss}
      onPublish={onPublish}
      exportStatus={exportStatus}
      exportSnippetsCompleted={exportSnippetsCompleted}
      publicationToPublish={publicationToPublish}
      totalExportSnippet={totalExportSnippet}
      isPearsonPlusTest={true}
      productIdPDZ={productIdPDZ}
      disabledTocLevel={disabledTocLevel}
    />}

    {isEpubOrChannelPartner && <ExportETextAndChannelPartner
      visible={exportModalVisible}
      outputType={publicationToPublish.outputType}
      market={publicationToPublish?.market}
      onDismiss={dismiss}
      onPublish={onPublish}
      exportStatus={exportStatus}
      exportSnippetsCompleted={exportSnippetsCompleted}
      publicationToPublish={publicationToPublish}
      totalExportSnippet={totalExportSnippet}
      disabledTocLevel={disabledTocLevel}
      getFeatureFlagValue={() => { }} />
    }
    <PublicationAlertModal
      visible={publicationAlertVisible}
      onPublish={publishAlertConfirm(publicationToPublish, 'PUBLISH')}
      onDismiss={publishAlertDismissHandler}
    />
    <DeleteConfirmModal
      visible={deleteModalVisible}
      onDismiss={dismiss}
      onDelete={onDelete}
    ></DeleteConfirmModal>
    <UnauthorizedModal
      visible={unauthorizedVisible}
      onDismiss={() => setUnauthorizedVisible(false)} />
  </>);
};

const mapStateToProps = createStructuredSelector({
  me: selectCurrentMe,
  publicationsList: getFilteredPublicationList,
  sortState: selectPublicationsSort,
});

const mapDispatchToProps = (dispatch) => ({
  setSort: (sortParam) => dispatch(setPublicationsSort(sortParam)),
  setHistory: (historyList, activityId, outputType, market) => dispatch(setPublicationsHistoryForActivity(historyList, activityId, outputType, market)),
  updatePublicationHistory: (publication) => dispatch(updatePublicationsList(publication, actionType.PUBLISH)),
  deletePublicationHistory: (publication) => dispatch(updatePublicationsList(publication, actionType.DELETE)),
  updatePublicationStatus: (publication) => batch(() => {
    dispatch(updatePublicationsStatus(publication));
    dispatch(updatePublicationsDetail(publication));
  }),
});

PublicationList.propTypes = {
  publicationList: PropTypes.array,
  theme: PropTypes.object,
  updatePublicationHistory: PropTypes.func,
  me: meShape,
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const flagNames = [
  featureKeys.WORKSPACE_PUBLICATION_LOCKTOC,
];
export default compose(withConnect, withTheme, enhanceWithFeatureFlagSubscription)(PublicationList, flagNames);
